import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { fmtTime } from "../format";
import CrmButton from "../ui/CrmButton";
import { FOCUS_RING } from "../ui/classes";
import NudgeEmailDialog from "./NudgeEmailDialog";
import { CRM } from "../../../constants/testIds/crm";

/** Nudge type → its sentence key(s) and action label (spec Zone 2 table). */
const NUDGE_COPY = Object.freeze({
  pack_ending: { one: "nudge.packEnding.one", many: "nudge.packEnding.many", action: "nudge.action.packEnding" },
  gone_quiet: { one: "nudge.goneQuiet", action: "nudge.action.goneQuiet" },
  new_client_checkin: { one: "nudge.newClients.one", many: "nudge.newClients.many", action: "nudge.action.newClients" },
  review_moment: { one: "nudge.reviewMoment", action: "nudge.action.reviewMoment" },
  unconfirmed_tomorrow: { one: "nudge.unconfirmedTomorrow", action: "nudge.action.unconfirmedTomorrow" },
  pathway_stuck: { one: "nudge.pathwayStuck.one", many: "nudge.pathwayStuck.many", action: "nudge.action.pathwayStuck" },
});

/**
 * The nudges whose action is an EDITABLE draft, reviewed in NudgeEmailDialog before it is
 * sent (backend `crm/nudges.py`: `pack_ending` and `gone_quiet` send the actor's text;
 * `pathway_stuck` sends it as the pathway encouragement, pathways-api-contract §10.8).
 * `subject: false`: the pathway encouragement e-mail has its own subject, so only the
 * body is edited and `{body_text}` is sent. `maxBody`: the body bound the server enforces
 * for that action (`ENCOURAGE_MESSAGE_MAX` = 2000 for the pathway encouragement; the
 * free-text nudges take `NudgeActionIn`'s 20000).
 */
const DRAFTED = Object.freeze({
  pack_ending: { title: "nudge.emailTitle", subject: true, maxBody: 20000 },
  gone_quiet: { title: "nudge.emailTitleGoneQuiet", subject: true, maxBody: 20000 },
  pathway_stuck: { title: "nudge.emailTitlePathwayStuck", subject: false, maxBody: 2000 },
});

/**
 * One nudge: one plain sentence + one action button + dismiss (×). The sentence is built
 * HERE from `type + vars` (C0.06: the server sends data, never copy). The free-text
 * actions (DRAFTED) open their pre-written email for review before it is sent; every
 * other action runs in one tap.
 */
export default function NudgeCard({ nudge, onAct, onDismiss }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [drafting, setDrafting] = useState(false);
  const copy = NUDGE_COPY[nudge.type];
  if (!copy) return null;
  const v = nudge.vars || {};
  const count = Number(
    nudge.type === "review_moment" ? (v.sessions_completed ?? v.nth ?? v.count)
      : (v.count ?? v.remaining ?? (nudge.client_ids || []).length),
  );
  const key = copy.many && count !== 1 ? copy.many : copy.one;
  const sentence = t(key, {
    name: v.client_name || "",
    n: count,
    weeks: v.weeks ?? "",
    time: v.slot_start ? fmtTime(v.slot_start, lang, v.timezone) : (v.time || ""),
    pathway: v.pathway_title || "",
  });
  const drafted = DRAFTED[nudge.type];

  // Already handled elsewhere is news, not a failure: the card is gone either way.
  const report = (e) => (apiErrorCode(e) === "NUDGE_CLOSED" ? toast.info : toast.error)(crmErrorMessage(e, t, tc));
  const dismiss = () => { onDismiss(nudge.id).catch(report); };
  const act = () => {
    if (drafted) { setDrafting(true); return; }
    onAct(nudge.id).then(() => toast.success(t("nudge.done"))).catch(report);
  };

  return (
    <article data-testid={CRM.nudge(nudge.id)} className="relative rounded-2xl bg-eh-cream px-5 py-4 pr-12">
      <p className="text-sm text-eh-ink">{sentence}</p>
      <CrmButton size="sm" className="mt-3" onClick={act} data-testid={CRM.nudgeAct(nudge.id)}>{t(copy.action)}</CrmButton>
      <button type="button" onClick={dismiss} aria-label={t("common.dismiss")} data-testid={CRM.nudgeDismiss(nudge.id)}
        className={`absolute right-4 top-4 rounded-full p-1 text-eh-muted hover:text-eh-ink ${FOCUS_RING}`}>
        <X size={16} aria-hidden="true" />
      </button>
      {drafting && (
        <NudgeEmailDialog nudgeId={nudge.id} title={t(drafted.title)} withSubject={drafted.subject} maxBody={drafted.maxBody}
          onClose={() => setDrafting(false)}
          onSend={(payload) => onAct(nudge.id, payload)
            .then(() => { setDrafting(false); toast.success(t("nudge.done")); })
            .catch((e) => {
              if (apiErrorCode(e) !== "NUDGE_CLOSED") throw e;
              setDrafting(false);
              report(e);
            })} />
      )}
    </article>
  );
}
